import React, { useEffect, useState } from 'react';
import { plants } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import axios from 'axios';
import PlantIdentifier from './PlantIdentifier';
import { properties } from '../services/api';
import GardenModal from './GardenModal';
import yardrayLogo from '../assets/yardray-logo.png';
import './PlantsList.css';

function PlantsList() {
  const [userPlants, setUserPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNurseries, setShowNurseries] = useState(false);
  const [nurseries, setNurseries] = useState([]);
  const [nurseriesLoading, setNurseriesLoading] = useState(false);
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState('all');
  const [showIdentifier, setShowIdentifier] = useState(false);
  const [userProperties, setUserProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState(localStorage.getItem('gardenbuddy_selected_property') || 'all');
  const [modal, setModal] = useState({ isOpen: false });

  useEffect(() => {
    loadPlants();
    loadUserProperties();
  }, []);

  const loadUserProperties = async () => {
    try {
      const res = await properties.getAll();
      setUserProperties(res.data.properties);
    } catch (err) {
      console.error('Error loading properties:', err);
    }
  };

  const loadPlants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await plants.getAll();

      const plantsWithPhotos = await Promise.all(
        response.data.plants.map(async (plant) => {
          try {
            const photosRes = await axios.get(`${process.env.REACT_APP_API_URL}/photos/plant/${plant.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            return {
              ...plant,
              firstPhoto: photosRes.data.photos[0]?.thumbnail_urls?.[0] || photosRes.data.photos[0]?.original_url
            };
          } catch (err) {
            return plant;
          }
        })
      );

      setUserPlants(plantsWithPhotos);
      setLoading(false);
    } catch (error) {
      console.error('Error loading plants:', error);
      setLoading(false);
    }
  };

  const handleDeletePlant = async (e, plantId) => {
    e.stopPropagation();
    setModal({ isOpen: true, type: 'confirm', message: 'Delete this plant? This cannot be undone.', onConfirm: async () => {
      setModal({ isOpen: false });
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${process.env.REACT_APP_API_URL}/plants/${plantId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        loadPlants();
      } catch (error) {
        setModal({ isOpen: true, type: 'alert', title: 'Error', message: 'Failed to delete plant', onConfirm: () => setModal({ isOpen: false }) });
      }
    }, onCancel: () => setModal({ isOpen: false }) });
  };

  const handlePlantIdentified = (plantData) => {
    if (!plantData) {
      setShowIdentifier(false);
      return;
    }
    navigate('/plants/add', { state: { prefilledData: plantData } });
    setShowIdentifier(false);
  };

  const findNurseries = async () => {
    setShowNurseries(true);
    setNurseriesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const property = userProperties.find(p => propertyFilter === 'all' || String(p.id) === String(propertyFilter)) || userProperties[0];
      if (!property) {
        setNurseriesLoading(false);
        setNurseries([]);
        return;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/google/places/nearby?lat=${property.latitude}&lng=${property.longitude}&type=garden_center&keyword=nursery`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNurseries(response.data.results || []);
    } catch (error) {
      console.error('Nursery search error:', error);
      setNurseries([]);
    } finally {
      setNurseriesLoading(false);
    }
  };

  const filteredPlants = userPlants.filter(plant => {
    const matchesSearch = plant.common_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plant.scientific_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plant.plant_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || plant.plant_type === filterType;
    const matchesProperty = propertyFilter === 'all' || String(plant.property_id) === String(propertyFilter);
    return matchesSearch && matchesType && matchesProperty;
  });

  if (loading) {
    return <div className="plants-loading">Loading plants...</div>;
  }

  return (
    <div className={`plants-container${isMobile ? ' plants-container--mobile' : ''}`}>
      <GardenModal isOpen={modal.isOpen} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} onCancel={() => setModal({ isOpen: false })} />

      {userProperties.length > 1 && (
        <div className="plants-prop-filter">
          <select
            className="plants-prop-select"
            value={propertyFilter}
            onChange={e => { setPropertyFilter(e.target.value); localStorage.setItem('gardenbuddy_selected_property', e.target.value); }}
          >
            <option value="all">All Properties</option>
            {userProperties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      )}

      <div className="plants-header">
        <h2>My Plants</h2>
        <div className="plants-btn-group">
          <button className="plants-identify-btn" onClick={() => setShowIdentifier(true)}>
            Identify Plant
          </button>
          <button className="plants-add-btn" onClick={() => navigate('/plants/add')}>
            + Add Plant
          </button>
          <button className="plants-nurseries-btn" onClick={findNurseries}>
            Find Nurseries
          </button>
        </div>
      </div>

      <div className="plants-search-wrap">
        <input
          className="plants-search-input"
          type="text"
          placeholder="Search plants..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="plants-filter-row">
          {[
            { value: 'all', label: 'All', count: userPlants.length },
            { value: 'vegetable', label: 'Vegetables', count: userPlants.filter(p => p.plant_type === 'vegetable').length },
            { value: 'fruit', label: 'Fruit', count: userPlants.filter(p => p.plant_type === 'fruit').length },
            { value: 'herb', label: 'Herbs', count: userPlants.filter(p => p.plant_type === 'herb').length },
            { value: 'flower', label: 'Flowers', count: userPlants.filter(p => p.plant_type === 'flower').length },
            { value: 'tree', label: 'Trees', count: userPlants.filter(p => p.plant_type === 'tree').length },
            { value: 'shrub', label: 'Shrubs', count: userPlants.filter(p => p.plant_type === 'shrub').length },
            { value: 'annual', label: 'Annuals', count: userPlants.filter(p => p.plant_type === 'annual').length },
            { value: 'perennial', label: 'Perennials', count: userPlants.filter(p => p.plant_type === 'perennial').length },
          ].map(filter => (
            <button
              key={filter.value}
              className={`plants-filter-btn${filterType === filter.value ? ' plants-filter-btn--active' : ''}`}
              onClick={() => setFilterType(filter.value)}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>

      <div className="plants-grid">
        {filteredPlants.map(plant => (
          <div
            key={plant.id}
            className="plants-card"
            onClick={() => navigate(`/plants/${plant.id}`)}
          >
            <div className="plants-card-photo">
              {plant.firstPhoto ? (
                <img
                  src={plant.firstPhoto}
                  alt={plant.common_name}
                  className="plants-card-photo-img"
                />
              ) : (
                '🌱'
              )}
            </div>

            <h3>{plant.common_name || 'Unknown Plant'}</h3>

            {plant.scientific_name && (
              <p className="plants-card-sci">{plant.scientific_name}</p>
            )}

            {plant.plant_type && (
              <span className="plants-type-badge">{plant.plant_type}</span>
            )}

            {plant.care_pruning_months && plant.care_pruning_months.includes(new Date().getMonth() + 1) && (
              <span className="plants-prune-badge">✂️ Prune now</span>
            )}

            {plant.date_planted && (
              <p className="plants-date">Planted: {new Date(plant.date_planted).toLocaleDateString()}</p>
            )}

            {plant.notes && (
              <p className="plants-notes">{plant.notes}</p>
            )}

            <button className="plants-delete-btn" onClick={(e) => handleDeletePlant(e, plant.id)}>
              🗑 Delete
            </button>
          </div>
        ))}
      </div>

      {showNurseries && (
        <div className="plants-nurseries-overlay">
          <div className="plants-nurseries-dialog">
            <div className="plants-nurseries-header">
              <h3>Local Nurseries</h3>
              <button className="plants-nurseries-close" onClick={() => setShowNurseries(false)}>✕</button>
            </div>
            <div className="plants-nurseries-body">
              {nurseriesLoading ? (
                <p className="plants-nurseries-loading">Searching nearby nurseries...</p>
              ) : nurseries.length === 0 ? (
                <div className="plants-nurseries-empty">
                  {userProperties.length === 0 ? (
                    <>
                      <img src={yardrayLogo} alt="Yardray" className="plants-nurseries-logo" />
                      <p>Add a property with an address to find nurseries near you.</p>
                      <button
                        className="plants-nurseries-add-btn"
                        onClick={() => { setShowNurseries(false); window.location.href='/properties'; }}
                      >
                        Add Property
                      </button>
                    </>
                  ) : (
                    <p>No nurseries found nearby.</p>
                  )}
                </div>
              ) : (
                <div className="plants-nurseries-list">
                  {nurseries.map((n, i) => (
                    <div key={i} className="plants-nursery-item">
                      <div className="plants-nursery-inner">
                        <div>
                          <p className="plants-nursery-name">{n.name}</p>
                          <p className="plants-nursery-address">{n.vicinity}</p>
                          {n.rating && (
                            <p className="plants-nursery-rating">
                              {'★'.repeat(Math.round(n.rating))} {n.rating} ({n.user_ratings_total} reviews)
                            </p>
                          )}
                        </div>
                        {n.opening_hours && (
                          <span className={`plants-hours-badge${n.opening_hours.open_now ? ' plants-hours-badge--open' : ' plants-hours-badge--closed'}`}>
                            {n.opening_hours.open_now ? 'Open' : 'Closed'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {filteredPlants.length === 0 && (
        <div className="plants-empty-state">
          <div className="plants-empty-icon">🌱</div>
          <p>No plants in this category yet.</p>
          <p>Add a plant or try a different filter.</p>
        </div>
      )}

      {showIdentifier && (
        <PlantIdentifier onPlantSelected={handlePlantIdentified} />
      )}
    </div>
  );
}

export default PlantsList;
